import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useHubStore, type HubFinding } from '../../store/useHubStore';
import { useOrgStore } from '../../store/useOrgStore';
import { FindingRow } from '../../components/shared/FindingRow';
import { severityRank, type Severity } from '../../components/shared/SeverityBar';
import { HubPageHeader } from '../../components/hub/HubPageHeader';
import toast from 'react-hot-toast';

const SEVERITIES: (Severity | 'all')[] = ['all', 'high', 'medium', 'low', 'info'];
const STATUSES: (HubFinding['status'] | 'all')[] = [
  'all',
  'open',
  'acknowledged',
  'snoozed',
  'dismissed',
  'bpx_in_progress',
];

export function HubFindingsPage() {
  const { findings, loading, loadFindings, setStatus, loadedForOrgId } = useHubStore();
  const orgUrl = useOrgStore((s) => s.orgUrl);
  const [severityFilter, setSeverityFilter] = useState<Severity | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<HubFinding['status'] | 'all'>('open');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const location = useLocation();

  // Initial load if needed (Phase 5 handles org-switch refetch on Overview;
  // this page does not duplicate that logic — just trigger a load if the
  // store is empty when the user lands here directly).
  // orgUrl is threaded in so loadedForOrgId is set correctly, preventing
  // Overview from seeing loadedForOrgId===null and refetching needlessly.
  useEffect(() => {
    if (findings.length === 0 && !loading && loadedForOrgId === null) {
      void loadFindings(orgUrl ?? undefined);
    }
  }, [findings.length, loading, loadedForOrgId, loadFindings, orgUrl]);

  // Scroll/highlight target finding from a #fragment deep link
  // (e.g. Overview clicks /hub/findings#abc123).
  useEffect(() => {
    const id = location.hash.startsWith('#') ? location.hash.slice(1) : '';
    if (!id) return;
    // Defer to after render — the rows are not in the DOM yet on first mount.
    const t = setTimeout(() => {
      const el = document.getElementById(`finding-${id}`);
      if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 100);
    return () => clearTimeout(t);
  }, [location.hash, findings.length]);

  const categories = useMemo(
    () => ['all', ...Array.from(new Set(findings.map((f) => f.category))).sort()],
    [findings],
  );

  const totals = useMemo(() => {
    const t = { high: 0, medium: 0, low: 0, info: 0, open: 0, bpx: 0, snoozed: 0 };
    for (const f of findings) {
      t[f.severity]++;
      if (f.status === 'open') t.open++;
      else if (f.status === 'bpx_in_progress') t.bpx++;
      else if (f.status === 'snoozed') t.snoozed++;
    }
    return t;
  }, [findings]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return [...findings]
      .filter((f) => severityFilter === 'all' || f.severity === severityFilter)
      .filter((f) => statusFilter === 'all' || f.status === statusFilter)
      .filter((f) => categoryFilter === 'all' || f.category === categoryFilter)
      .filter(
        (f) =>
          q === '' ||
          f.title.toLowerCase().includes(q) ||
          (f.context ?? '').toLowerCase().includes(q),
      )
      .sort((a, b) => severityRank(a.severity) - severityRank(b.severity));
  }, [findings, severityFilter, statusFilter, categoryFilter, search]);

  const safeSetStatus = async (
    id: string,
    status: HubFinding['status'],
    extra?: { snoozeUntil?: number | null; bpxRequestId?: string | null },
  ) => {
    try {
      await setStatus(id, status, extra);
    } catch (err) {
      const m =
        (err as { response?: { data?: { error?: string } } }).response?.data?.error ??
        (err as Error).message;
      toast.error(m ?? 'Could not update finding status');
    }
  };

  return (
    <section className="space-y-6 max-w-5xl">
      <HubPageHeader
        title="Findings & Alerts"
        subtitle={
          <>
            {findings.length} total · <span className="text-danger">{totals.high} high</span> ·{' '}
            <span className="text-warning">{totals.medium} medium</span> · {totals.low} low
          </>
        }
        actions={
          <>
            <Pill>{totals.open} open</Pill>
            <Pill tone="blue">{totals.bpx} with TYRL</Pill>
            <Pill tone="muted">{totals.snoozed} snoozed</Pill>
          </>
        }
      />

      <div className="flex flex-wrap gap-2 text-xs items-center">
        <Select
          label="Severity"
          value={severityFilter}
          onChange={(v) => setSeverityFilter(v as Severity | 'all')}
          options={SEVERITIES}
        />
        <Select label="Category" value={categoryFilter} onChange={setCategoryFilter} options={categories} />
        <Select
          label="Status"
          value={statusFilter}
          onChange={(v) => setStatusFilter(v as HubFinding['status'] | 'all')}
          options={STATUSES}
        />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search findings…"
          aria-label="Search findings"
          className="ml-auto px-3 py-1.5 rounded-md border border-border bg-surface-2 text-xs w-56 text-text placeholder:text-text-subtle focus:outline-none focus:border-border-strong"
        />
      </div>

      {loading && filtered.length === 0 ? (
        <p className="text-text-muted text-sm">Loading…</p>
      ) : (
        <div className="flex flex-col gap-2">
          {filtered.map((f) => (
            <div key={f.id} id={`finding-${f.id}`}>
              <FindingRow
                id={f.id}
                severity={f.severity}
                category={f.category}
                title={f.title}
                context={f.context}
                status={f.status}
                caseRef={f.caseRef ?? undefined}
                ctas={
                  <FindingActions
                    finding={f}
                    onSnooze={() =>
                      safeSetStatus(f.id, 'snoozed', {
                        snoozeUntil: Date.now() + 30 * 24 * 60 * 60 * 1000,
                      })
                    }
                    onDismiss={() => safeSetStatus(f.id, 'dismissed')}
                    onReopen={() => safeSetStatus(f.id, 'open')}
                    onRequestBPX={() => {
                      // Phase 10 wires this to the GetHelp form.
                      window.location.href = `/hub/get-help?findingId=${encodeURIComponent(f.id)}`;
                    }}
                  />
                }
              />
            </div>
          ))}
          {filtered.length === 0 && (
            <p className="text-text-muted text-sm py-4">No findings match.</p>
          )}
        </div>
      )}
    </section>
  );
}

function Select({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: readonly string[];
}) {
  return (
    <label className="flex items-center gap-1.5">
      <span className="text-text-muted">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="px-2.5 py-1.5 rounded-md border border-border bg-surface-2 text-text focus:outline-none focus:border-border-strong"
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}

function Pill({ children, tone }: { children: React.ReactNode; tone?: 'blue' | 'muted' }) {
  const cls =
    tone === 'blue'
      ? 'border-sfblue/40 bg-sfblue/15 text-sfblue'
      : tone === 'muted'
        ? 'border-border-subtle text-text-muted'
        : 'border-border-subtle text-text';
  return <span className={`px-2.5 py-1 rounded-md border ${cls} text-xs`}>{children}</span>;
}

function FindingActions({
  finding,
  onSnooze,
  onDismiss,
  onReopen,
  onRequestBPX,
}: {
  finding: HubFinding;
  onSnooze: () => void;
  onDismiss: () => void;
  onReopen: () => void;
  onRequestBPX: () => void;
}) {
  const btn = 'px-2.5 py-1 rounded-md border text-xs transition-colors';
  if (finding.status === 'snoozed' || finding.status === 'dismissed') {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onReopen();
        }}
        className={`${btn} border-border bg-surface-1 text-text hover:bg-surface-hover hover:border-border-strong`}
      >
        Reopen
      </button>
    );
  }
  if (finding.status === 'bpx_in_progress') {
    return (
      <span
        className={`${btn} border-sfblue/40 bg-sfblue/15 text-sfblue cursor-default select-none`}
        title="This finding is being handled by TYRL."
      >
        View case
      </span>
    );
  }
  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRequestBPX();
        }}
        className={`${btn} border-sfblue/40 bg-sfblue/15 text-sfblue hover:bg-sfblue/25 hover:border-sfblue/60`}
      >
        Request TYRL help
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onSnooze();
        }}
        className={`${btn} border-border bg-surface-1 text-text-muted hover:text-text hover:bg-surface-hover hover:border-border-strong`}
      >
        Snooze
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDismiss();
        }}
        className={`${btn} border-border bg-surface-1 text-text-subtle hover:text-text-muted hover:bg-surface-hover hover:border-border-strong`}
      >
        Dismiss
      </button>
    </>
  );
}
